import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import { PageHeader, Card, AtomBadge, StatusPill } from "../components/primitives";
import { formatNGN } from "../lib/format";
import jsPDF from "jspdf";
import { useAuth } from "../lib/auth";

export default function STRDetail() {
  const { sid } = useParams();
  const [data, setData] = useState(null);
  const [editing, setEditing] = useState(false);
  const [signoffs, setSignoffs] = useState(null);
  const { institution, user } = useAuth();

  useEffect(() => {
    api.get(`/reports/str/${sid}`).then(r => setData(r.data));
    api.get(`/reporting/str/${sid}/signoffs`).then(r => setSignoffs(r.data)).catch(() => {});
  }, [sid]);
  if (!data) return <div className="p-6 text-[13px] text-ink-muted">Loading STR…</div>;
  const { str: s, narrative, typology } = data;
  const inst = institution || { name: "Acme Demo Bank Plc", rc: "RC 982341", frc: "FRC/2018/0000045231", cbn_licence: "CBN/DMB/ACM/2014", address: "12 Acme Avenue, Victoria Island, Lagos", mlro: s.mlro };

  const submit = () => {
    alert(`Submission acknowledged by NFIU at ${new Date().toLocaleTimeString("en-GB",{hour:"2-digit",minute:"2-digit"})} WAT · reference NFIU-STR-26-${Math.floor(Math.random()*99999)}`);
  };

  const downloadGoamlXML = async () => {
    const apiBase = process.env.REACT_APP_BACKEND_URL;
    window.open(`${apiBase}/api/reporting/str/${sid}/goaml-xml`, "_blank");
  };

  const recordSignoff = async (role, decision) => {
    const comment = decision === "reject" || decision === "request_changes" ? window.prompt(`${decision} reason:`, "") : null;
    if ((decision === "reject" || decision === "request_changes") && !comment) return;
    try {
      const r = await api.post(`/reporting/str/${sid}/signoff`, { reviewer: user?.name || role, role, decision, comment });
      // refresh
      const r2 = await api.get(`/reporting/str/${sid}/signoffs`);
      setSignoffs(r2.data);
    } catch(e) { alert("Failed to record signoff"); }
  };

  const exportPDF = () => {
    const doc = new jsPDF();
    doc.setFontSize(14);
    doc.text(`${inst.name} — Suspicious Transaction Report`, 14, 16);
    doc.setFontSize(9);
    doc.text(`${s.id} · Drafted ${s.drafted} · NFIU ref ${s.nfiu_ref || 'DRAFT'}`, 14, 22);
    doc.line(14, 26, 196, 26);
    doc.setFontSize(11);
    doc.text(`Section A — Reporting institution`, 14, 36);
    doc.setFontSize(9);
    doc.text([`Name: ${inst.name}`, `RC: ${inst.rc}`, `FRC: ${inst.frc}`, `CBN licence: ${inst.cbn_licence}`, `Address: ${inst.address}`, `MLRO: ${inst.mlro}`], 14, 42);
    doc.setFontSize(11);
    doc.text(`Section B — Subject`, 14, 78);
    doc.setFontSize(9);
    doc.text([`Name: ${s.subject}`, `Cust ID: ${s.subject_cid}`, `Amount at risk: ${formatNGN(s.amount)}`, `Typology: ${typology.amltrix} - ${typology.name}`], 14, 84);
    doc.setFontSize(11);
    doc.text(`Section D — Narrative`, 14, 112);
    doc.setFontSize(9);
    doc.text(doc.splitTextToSize(narrative, 180), 14, 118);
    doc.save(`${s.id}.pdf`);
  };

  return (
    <div>
      <PageHeader
        title={s.id}
        subtitle={`${typology.name} · Drafted ${s.drafted}`}
        sections={["5.8"]}
        atom={<AtomBadge model="Atom Nucleus" version="v3.5" description="STR narrative drafted by Atom Nucleus" />}
        right={
          <div className="flex gap-2">
            <button onClick={exportPDF} data-testid="export-pdf-btn" className="px-3 py-1.5 text-[12px] border border-line rounded-full hover:bg-bg-hover">Export PDF</button>
            <button onClick={downloadGoamlXML} data-testid="goaml-xml-btn" className="px-3 py-1.5 text-[12px] border border-amber/40 bg-amber/8 text-amber-700 rounded-full hover:bg-amber/15">Download goAML XML</button>
            {!s.filed && <button onClick={submit} data-testid="submit-nfiu-btn" className="px-3 py-1.5 text-[12px] bg-ink text-white rounded-full hover:bg-navy">Submit to NFIU</button>}
          </div>
        }
      />
      <div className="p-6 grid grid-cols-3 gap-3">
        <div className="col-span-2 space-y-3">
          <Card>
            <div className="px-4 py-3 border-b border-line text-[14px] font-medium">Section A — Reporting institution</div>
            <div className="p-4 text-[12px] space-y-1.5">
              {[["Name", inst.name],["RC number", inst.rc],["FRC number", inst.frc],["CBN licence", inst.cbn_licence],["Address", inst.address],["MLRO", inst.mlro]].map(([k,v]) => <div key={k} className="flex justify-between border-b border-line/50 py-1"><span className="text-ink-subtle">{k}</span><span>{v}</span></div>)}
            </div>
          </Card>
          <Card>
            <div className="px-4 py-3 border-b border-line text-[14px] font-medium">Section B — Subject of the report</div>
            <div className="p-4 text-[12px] space-y-1.5">
              <div className="flex justify-between border-b border-line/50 py-1"><span className="text-ink-subtle">Subject</span><Link to={`/customers/${s.subject_cid}`} className="text-amber-600 hover:underline">{s.subject}</Link></div>
              <div className="flex justify-between border-b border-line/50 py-1"><span className="text-ink-subtle">Cust ID</span><span className="hash-mono">{s.subject_cid}</span></div>
              <div className="flex justify-between border-b border-line/50 py-1"><span className="text-ink-subtle">Period</span><span>22 Apr 2026 — 22 May 2026 WAT</span></div>
              <div className="flex justify-between border-b border-line/50 py-1"><span className="text-ink-subtle">Total amount involved</span><span className="font-medium tabular">{formatNGN(s.amount)}</span></div>
              <div className="flex justify-between border-b border-line/50 py-1"><span className="text-ink-subtle">Activity type</span><span>{typology.name}</span></div>
              <div className="flex justify-between border-b border-line/50 py-1"><span className="text-ink-subtle">AMLTRIX</span><span>{typology.amltrix}</span></div>
            </div>
          </Card>
          <Card>
            <div className="px-4 py-3 border-b border-line flex items-center justify-between">
              <div className="text-[14px] font-medium">Section D — Narrative</div>
              <AtomBadge model="Atom Nucleus" version="v3.5" />
            </div>
            <div className="p-4 border-l-2 border-amber/40 bg-amber/4 m-4 text-[13px] leading-relaxed whitespace-pre-wrap">
              {editing ? <textarea defaultValue={narrative} className="w-full bg-transparent text-[13px] outline-none" rows={20} /> : narrative}
            </div>
            <div className="px-4 pb-4 flex items-center justify-between text-[11px] text-ink-subtle">
              <span>Drafted by Atom Nucleus v3.5 — please review and edit before submission.</span>
              <button onClick={() => setEditing(!editing)} className="text-amber-600 hover:underline">{editing ? "Save edits" : "Edit narrative"}</button>
            </div>
          </Card>
        </div>
        <div className="col-span-1 space-y-3">
          <Card>
            <div className="px-4 py-3 border-b border-line text-[14px] font-medium">Multi-reviewer signoff</div>
            <div className="p-3 text-[12px]">
              <div className="flex items-center justify-between mb-2 pb-2 border-b border-line/50">
                <span className="text-ink-subtle">Status</span>
                <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${
                  signoffs?.status === "Approved for filing" ? "bg-risk-low/15 text-risk-low" :
                  signoffs?.status === "Rejected" ? "bg-risk-veryhigh/15 text-risk-veryhigh" :
                  "bg-risk-medium/15 text-risk-medium"
                }`}>{signoffs?.status || "Awaiting review"}</span>
              </div>
              <div className="space-y-2">
                {["MLRO","CCO","Legal"].map(role => {
                  const existing = signoffs?.signoffs?.find(x => x.role === role);
                  return (
                    <div key={role} className="border border-line rounded-lg p-2.5">
                      <div className="flex items-center justify-between mb-1.5">
                        <span className="text-[11px] font-medium">{role}</span>
                        {existing ? (
                          <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${
                            existing.decision === "approve" ? "bg-risk-low/15 text-risk-low" :
                            existing.decision === "reject" ? "bg-risk-veryhigh/15 text-risk-veryhigh" :
                            "bg-risk-medium/15 text-risk-medium"
                          }`}>{existing.decision}</span>
                        ) : <span className="text-[10px] text-ink-subtle">Pending</span>}
                      </div>
                      {existing ? (
                        <div className="text-[10px] text-ink-muted">{existing.reviewer} · {existing.ts}{existing.comment ? ` · ${existing.comment}` : ""}</div>
                      ) : (
                        <div className="flex gap-1">
                          <button data-testid={`signoff-${role}-approve`} onClick={() => recordSignoff(role, "approve")} className="flex-1 px-2 py-1 text-[10px] bg-risk-low text-white rounded font-medium hover:opacity-90">Approve</button>
                          <button data-testid={`signoff-${role}-reject`} onClick={() => recordSignoff(role, "reject")} className="flex-1 px-2 py-1 text-[10px] border border-line rounded hover:bg-bg-hover">Reject</button>
                          <button data-testid={`signoff-${role}-changes`} onClick={() => recordSignoff(role, "request_changes")} className="flex-1 px-2 py-1 text-[10px] border border-line rounded hover:bg-bg-hover">Changes</button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              <div className="mt-2 pt-2 border-t border-line text-[10px] text-ink-subtle">
                All three approvals required before NFIU submission. Each decision recorded in audit log.
              </div>
            </div>
          </Card>
          <Card>
            <div className="px-4 py-3 border-b border-line text-[14px] font-medium">Submission</div>
            <div className="p-4 text-[12px] space-y-2">
              <div className="flex justify-between"><span className="text-ink-subtle">Status</span><StatusPill status={s.status} /></div>
              <div className="flex justify-between"><span className="text-ink-subtle">Filed</span><span>{s.filed || 'Draft'}</span></div>
              <div className="flex justify-between"><span className="text-ink-subtle">NFIU ref</span><span className="hash-mono">{s.nfiu_ref || '—'}</span></div>
              <div className="flex justify-between"><span className="text-ink-subtle">CBN section</span><span>5.8</span></div>
              <div className="flex justify-between"><span className="text-ink-subtle">FATF Rec.</span><span>Rec. 20</span></div>
              <div className="flex justify-between"><span className="text-ink-subtle">Statute</span><span>MLPA s. 6(2)</span></div>
            </div>
          </Card>
          <Card>
            <div className="px-4 py-3 border-b border-line text-[14px] font-medium">Section E — Evidence</div>
            <div className="p-3 text-[12px] space-y-1.5">
              <div className="border border-line rounded-sm p-2">Implicated transactions · auto-attached from case</div>
              <div className="border border-line rounded-sm p-2">Screening result · pulled at draft time</div>
              <div className="border border-line rounded-sm p-2">Customer profile snapshot</div>
              <div className="border border-line rounded-sm p-2">Network analysis · Atom Nucleus v3.5</div>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}
